import axios, { AxiosInstance } from 'axios'
import { uuid } from 'vue-uuid'
import { queueNetworkErrorSnackbar } from '@/util/api-error-snackbar'

export const DEFAULT_API_CLIENT_TIMEOUT = 10000
export const FILE_TIMEOUT = 500000
export const SPACE_DUPLICATION_TIMEOUT = 500000
/** Match notch-pdf-generator production `pdf.renderTimeoutMs` (see docs/canvas-load/README.md). */
export const PDF_GENERATION_TIMEOUT = 180_000

// List of paths that do not require authentication (no token check needed)
// Ant Design-like wildcards (*, **, ?) are supported for path matching.
const publicPaths: string[] = [
  '/crm/hubspot/createspace',
  '/crm/hubspot/autocomplete',
  '/crm/hubspot/spacelists',
  '/users/refreshtoken',
  '/login',
  '/users/register',
  '/users/register-with-google',
  '/users/login-with-google',
  '/users/login-as-anonymous',
  '/users/registerFromInvite',
  '/users/validateRegisterFromInvite',
  '/users/sendPasswordResetEmail',
  '/users/resend-pending-invitation',
  '/users/validatePasswordReset',
  '/users/changePassword',
  '/users/checkToken',
  '/users/register-verification',
  '/crm/salesforce/widget/**',
  '/spaces/*/verify-email',
  '/spaces/*/is-public',
  '/spaces/*/verification-screen-data'
]

function getRequestPath(url: string): string {
  if (!url) {
    return ''
  }

  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return new URL(url).pathname
    }
  } catch {
    // fall through to relative path parsing
  }

  return url.split('?')[0]
}

function createPublicPathPatterns(paths: string[]): RegExp[] {
  return paths.map(pattern => {
    const regexPattern = pattern
      .replace(/\//g, '\\/')
      .replace(/\*\*/g, '.+')
      .replace(/\*/g, '[^/]+')
      .replace(/\?/g, '[^/]')
    return new RegExp(`^${regexPattern}$`)
  })
}

function isPublicPath(url: string, patterns: RegExp[]): boolean {
  const path = getRequestPath(url)
  return Boolean(path) && patterns.some(pattern => pattern.test(path))
}

class ApiClientService {
  readonly apiClient: AxiosInstance

  async getToken() {
    // wait if refresh is currently running
    const authStore = (await import('@/modules/auth/stores/AuthStore')).useAuthStore()
    await authStore.refreshTokenPromise
    return authStore.getToken()!
  }

  constructor() {
    const apiClient = axios.create({
      baseURL: import.meta.env.VITE_BASE_URL,
      timeout: DEFAULT_API_CLIENT_TIMEOUT
    })

    function createAntPathPatterns(paths: string[]): RegExp[] {
      return createPublicPathPatterns(paths)
    }

    const placeholderPatterns: RegExp[] = createAntPathPatterns(publicPaths)

    apiClient.interceptors.request.use(
      async request => {
        const url = request.url ?? ''
        const isPublicRequest = isPublicPath(url, placeholderPatterns)

        const traceId = uuid.v4()
        request.headers['X-Trace-ID'] = traceId
        localStorage.setItem('traceId', traceId)
        if (isPublicRequest) {
          return request
        } else {
          const authStore = (await import('@/modules/auth/stores/AuthStore')).useAuthStore()
          const tokenExpiry = authStore.getTokenExpiry()?.getTime()
          const inFiveMinutes = Date.now() + 5 * 60 * 1000
          const isTokenExpiredOrSoonExpiring = tokenExpiry != null && tokenExpiry < inFiveMinutes
          if (isTokenExpiredOrSoonExpiring) {
            // A failed refresh used to fall through and send the stale (or `undefined`) credential,
            // guaranteeing a 401 the response interceptor then retried forever. End the session here
            // instead of letting the doomed request go out.
            const refreshed = await authStore.startTokenRefresh()
            if (!refreshed) {
              void authStore.logout()
              return Promise.reject(new Error('Session expired'))
            }
          }
          request.headers.Authorization = await this.getToken()
          return request
        }
      },
      error => error
    )

    // A string key, not a Symbol: axios' `mergeConfig` rebuilds the config for the retried request
    // from `Object.keys(...)`, which skips symbol keys — the flag was silently dropped and the 401
    // retry guard never latched.
    const RETRY_FLAG = '__notchAuthRetry'

    apiClient.interceptors.response.use(
      async response => {
        return response
      },
      async error => {
        const originalRequest = error.config
        const authStore = (await import('@/modules/auth/stores/AuthStore')).useAuthStore()
        const requestUrl = originalRequest?.url ?? ''
        const isPublicRequest = isPublicPath(requestUrl, placeholderPatterns)

        // Avoid logout ↔ tracking/refresh loops while a logout is already in progress.
        if (authStore.logoutLoading) {
          return Promise.reject(error)
        }

        // A response-less Axios error means the request did not complete (offline, DNS failure or
        // timeout). Defer the generic message so a caller with a contextual retry can claim this
        // error first; unhandled failures still give the user feedback.
        queueNetworkErrorSnackbar(error)

        // `error.config` is absent for a request that never went out (cancelled in-flight XHRs on
        // logout, DNS/network failures). Dereferencing it raw threw a TypeError from inside this
        // interceptor and replaced the real error for every caller downstream.
        if (requestUrl === '/users/refreshtoken') {
          authStore.logout()
          return Promise.reject(error)
        }

        // NS-205: soft-locked trial teams get a 403 with a stable code. Surface an upgrade nudge
        // instead of a silent failure (the UI hides gated actions; this is only the safety net).
        //
        // NS-203: `plan-entitlement-required` is the same safety net for a plan that simply does not
        // include the feature. Handled here rather than per call site, because each one degraded
        // differently — a console-only error in the content library, the BE's raw English rendered as
        // a name-field validation error on space lists, a generic snackbar on branding — and BE
        // Phase 2.3 adds eight more. Soft lock is checked first: a soft-locked team is also
        // unentitled, and the trial story is the more specific one to tell.
        if (!isPublicRequest && error.response?.status === 403) {
          const { isTrialSoftLockedError, handleTrialSoftLockedError } = await import(
            '@/modules/featurerestriction/trialSoftLock'
          )
          if (isTrialSoftLockedError(error)) {
            void handleTrialSoftLockedError()
            return Promise.reject(error)
          }

          const { isPlanEntitlementError, handlePlanEntitlementError } = await import(
            '@/modules/featurerestriction/planEntitlement'
          )
          if (isPlanEntitlementError(error)) {
            void handlePlanEntitlementError()
            return Promise.reject(error)
          }
        }

        if (error.response?.status === 401 && originalRequest && !originalRequest[RETRY_FLAG] && !isPublicRequest) {
          originalRequest[RETRY_FLAG] = true

          // `startTokenRefresh()` handles every failure internally, so it practically never rejects —
          // branching on its result is what actually reaches logout here.
          const refreshed = await authStore.startTokenRefresh()
          if (!refreshed) {
            void authStore.logout()
            return Promise.reject(error)
          }

          originalRequest.headers['Authorization'] = await this.getToken()

          return this.apiClient(originalRequest)
        }
        return Promise.reject(error)
      }
    )

    this.apiClient = apiClient
  }
}

export default new ApiClientService().apiClient
